import React, { useState, useEffect } from 'react';
import { ChevronRight, Upload, UserCircle, FileText, CheckCircle } from 'lucide-react';

const HomePage = ({ setCurrentPage, isLoggedIn }) => {
  const [successMessage, setSuccessMessage] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Removing unused state variable
  // const [isTransitioning, setIsTransitioning] = useState(false);
  const images = [
    { src: '/img/main img.png', alt: 'Taking a picture of the menu' },
    { src: '/img/menu output.png', alt: 'Menu analysis output' }
  ];

  // Check for messages in localStorage when component mounts
  useEffect(() => {
    // Check for both localStorage message and URL parameter
    const message = localStorage.getItem('auth_message');
    const wasAccountDeleted = localStorage.getItem('account_deleted');
    const urlParams = new URLSearchParams(window.location.search);
    const deletedParam = urlParams.get('deleted');
    
    // If we have a message and either the account_deleted flag or the URL parameter
    if (message && (wasAccountDeleted === 'true' || deletedParam === 'true')) {
      setSuccessMessage(message);
      // Clear the messages after showing them
      localStorage.removeItem('auth_message');
      localStorage.removeItem('account_deleted');
      
      // Remove the URL parameter by replacing the current URL without reloading
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Image carousel effect with smooth transition
  useEffect(() => {
    const interval = setInterval(() => {
      // Start transition (no need for isTransitioning state)
      
      // After transition completes, change the image
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => 
          prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
        // Image changes with CSS transitions based on opacity
      }, 1000); // This should match the CSS transition duration
    }, 5000); // Total time between image changes (transition + display time)
    
    return () => clearInterval(interval);
  }, [images.length]); // Adding images.length as a dependency

  return (
    <div className="flex flex-col">
      {/* Success Message (if present) */}
      {successMessage && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" />
            {successMessage}
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <h1 className="text-4xl font-bold mb-4">Eat Smart with AI Menu Analysis</h1>
              <p className="text-xl mb-8">Upload any restaurant menu and get personalized healthy recommendations based on your dietary needs and preferences.</p>
              <button 
                onClick={() => setCurrentPage(isLoggedIn ? 'app' : 'auth')} 
                className="bg-white text-emerald-600 hover:bg-emerald-50 font-bold py-3 px-6 rounded-lg shadow-lg transition duration-300"
              >
                {isLoggedIn ? 'Start Analyzing' : 'Get Started'}
                <ChevronRight className="inline ml-1 h-5 w-5" />
              </button>
            </div>
            <div className="md:w-1/2 flex justify-center">
              <div className="rounded-lg shadow-xl overflow-hidden max-w-md w-full">
                {/* Main image container */}
                <div className="overflow-hidden relative" style={{ height: '300px' }}>
                  {/* All images positioned absolutely with transitions */}
                  {images.map((image, index) => (
                    <div 
                      key={index}
                      className="absolute top-0 left-0 right-0 w-full h-full transition-opacity duration-1000 ease-in-out overflow-hidden"
                      style={{ 
                        opacity: currentImageIndex === index ? 1 : 0,
                        zIndex: currentImageIndex === index ? 10 : 0
                      }}
                    >
                      <img 
                        src={image.src} 
                        alt={image.alt} 
                        className="w-full object-cover" 
                        style={{ objectPosition: '50% 0%' }} /* Position center-top */
                      />
                    </div>
                  ))}
                </div>
                
                {/* Navigation dots - positioned outside image container */}
                <div className="flex justify-center items-center py-0.5">
                  {images.map((_, index) => (
                    <button 
                      key={index}
                      className={`h-2 w-2 rounded-full mx-1 transition-colors duration-300 ${index === currentImageIndex ? 'bg-emerald-600' : 'bg-gray-300'}`}
                      onClick={() => {
                        if (currentImageIndex !== index) {
                          setCurrentImageIndex(index);
                        }
                      }}
                      aria-label={`View image ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Features Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">How It Works</h2>
            <p className="text-lg text-gray-600">Simple steps to healthier restaurant choices</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Feature 1 */}
            <div className="flex flex-col items-center text-center">
              <div className="bg-emerald-100 p-5 rounded-full mb-6">
                <UserCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Set Your Preferences</h3>
              <p className="text-gray-600">Tell us about your dietary needs, restrictions, and health goals.</p>
            </div>
            
            {/* Feature 2 */}
            <div className="flex flex-col items-center text-center">
              <div className="bg-emerald-100 p-5 rounded-full mb-6">
                <Upload className="h-10 w-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Upload Menu</h3>
              <p className="text-gray-600">Take a photo or upload a PDF of nearly any restaurant menu.</p>
            </div>
            
            {/* Feature 3 */}
            <div className="flex flex-col items-center text-center">
              <div className="bg-emerald-100 p-5 rounded-full mb-6">
                <FileText className="h-10 w-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Get Recommendations</h3>
              <p className="text-gray-600">Receive personalized menu suggestions with detailed nutritional insights and explanations.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Value Proposition Section */}
      <div className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Why MenuAnalyzer.AI?</h2>
            <p className="text-lg text-gray-600">Smart solutions for every dining challenge</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Hook 1 */}
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-emerald-600 mb-4">Menus decoded. Diets defended.</h3>
              <p className="text-gray-600">Sticking to a diet or eating healthy can be tough when you're out at a restaurant; menus are full of tempting choices that may not match your goals! We'll scan the whole menu and highlight the items that align with your nutrition preferences—making it easier to stay on track without stress.</p>
            </div>
            
            {/* Hook 2 */}
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-emerald-600 mb-4">Menu minefield? We'll clear the path.</h3>
              <p className="text-gray-600">Dining out means constant vigilance for anyone with food allergies or sensitivities. We'll quickly filter out and bring forward only the items that meet your dietary restrictions—so you can order with confidence and peace of mind.</p>
            </div>
            
            {/* Hook 3 */}
            <div className="bg-white p-8 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-emerald-600 mb-4">Break the routine. Discover new bites.</h3>
              <p className="text-gray-600">Find yourself ordering the same dish on repeat? We'll make exploration easy by surfacing new items that fit your tastes and needs, helping you discover options you might have otherwise missed.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="bg-emerald-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to eat healthier?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">Join others making informed food choices every day with MenuAnalyzer.AI.</p>
          <button 
            onClick={() => setCurrentPage(isLoggedIn ? 'app' : 'auth')} 
            className="bg-white text-emerald-600 hover:bg-emerald-50 font-bold py-3 px-8 rounded-lg shadow-lg transition duration-300 text-lg"
          >
            {isLoggedIn ? 'Go to App' : 'Sign Up Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
